import type { ColumnGroup } from '@/components/table/table-core';
import {
  type ColumnAttributes,
  LockableField,
} from '@/components/table/table-types';
import type { TableColumn } from './types/create-price-table-types';

export const getColumnData = (
  lockedColumn: LockableField | undefined,
  isApproveMode = false,
  isReadOnly = false,
): Record<TableColumn, ColumnAttributes> => ({
  productId: { label: 'SKU ID', size: 70, alignment: 'left' },
  productName: { label: 'SKU Description', size: 120, alignment: 'left' },
  priceUnitId: { label: 'Price list', size: 60, alignment: 'left' },
  priceUnitName: {
    label: 'Price list description',
    size: 110,
    alignment: 'left',
  },
  themeName: { label: 'Theme', size: 90, alignment: 'left' },
  productAudience: {
    label: 'Audience',
    size: 90,
    hidden: true,
    alignment: 'left',
  },
  localLaunchDate: {
    label: 'Local Launch Date',
    size: 100,
    hidden: true,
    alignment: 'left',
  },
  localExitDate: {
    label: 'Local Exit Date',
    size: 100,
    hidden: true,
    alignment: 'left',
  },
  productGroup: {
    label: 'Product Group',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  subMarketGroupName: {
    label: 'Product BU',
    size: 90,
    hidden: true,
    alignment: 'left',
  },
  groupName: { label: 'Group', size: 120, hidden: true, alignment: 'left' },
  productCategory: {
    label: 'Product Category',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  salesChannel: {
    label: 'Sales Channel',
    size: 90,
    hidden: true,
    alignment: 'left',
  },
  license: { label: 'License', size: 60, hidden: true, alignment: 'left' },
  productClassLvl1: {
    label: 'Product Class Lvl. 1',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  productClassLvl2: {
    label: 'Product Class Lvl. 2',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  developmentOwnershipName: {
    label: 'Development Ownership',
    size: 170,
    hidden: true,
    alignment: 'left',
  },
  productCompositionName: {
    label: 'Product Composition',
    size: 160,
    hidden: true,
    alignment: 'left',
  },
  superSegmentName: {
    label: 'Super Segment',
    size: 130,
    hidden: true,
    alignment: 'left',
  },
  themeCategoryName: {
    label: 'Theme Category',
    size: 130,
    hidden: true,
    alignment: 'left',
  },
  isRareItem: {
    label: 'Rare Item',
    size: 100,
    hidden: true,
    alignment: 'left',
  },
  boxSizeName: {
    label: 'Box Size',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  businessToBusinessLaunchDate: {
    label: 'B2B Launch Date',
    size: 130,
    hidden: true,
    alignment: 'left',
  },
  directToConsumerLifelong: {
    label: 'D2C Lifelong',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  sizeDimension: {
    label: 'Size',
    size: 100,
    hidden: true,
    alignment: 'left',
  },
  pieceCount: {
    label: 'Piece Count',
    size: 110,
    hidden: true,
    alignment: 'right',
  },
  smartPlayName: {
    label: 'Smart Play',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  playTypeName: {
    label: 'Play Type',
    size: 140,
    hidden: true,
    alignment: 'left',
  },
  topThemeName: {
    label: 'Top Theme',
    size: 120,
    hidden: true,
    alignment: 'left',
  },
  launchStatus: { label: 'Status', size: 55, alignment: 'left' },
  primaryKey: { label: 'Key', size: 50, hidden: true, alignment: 'left' },
  localLeadRrp: { label: 'LL RRP', size: 70, alignment: 'right' },
  localLeadLp: {
    label: 'LL LP',
    size: 70,
    hidden: true,
    alignment: 'right',
  },
  localLeadCurrency: {
    label: 'LL Currency',
    size: 80,
    hidden: true,
    alignment: 'right',
  },
  localLeadCurrencyRate: {
    label: 'IER',
    size: 50,
    alignment: 'right',
    hidden: true,
  },
  rrp: {
    label: 'RRP',
    size: 60,
    columnType:
      isReadOnly || isApproveMode || lockedColumn === LockableField.rrp
        ? undefined
        : 'editable',
    alignment: 'right',
  },
  rm: {
    label: 'RM %',
    size: 60,
    columnType:
      isReadOnly || isApproveMode || lockedColumn === LockableField.rm
        ? undefined
        : 'editable',
    alignment: 'right',
  },
  lp: {
    label: 'LP',
    size: 60,
    columnType:
      isReadOnly || isApproveMode || lockedColumn === LockableField.lp
        ? undefined
        : 'editable',
    alignment: 'right',
  },
  rrpCurrency: {
    label: 'RRP Currency',
    size: 80,
    hidden: true,
    alignment: 'right',
  },
  vat: { label: 'VAT %', size: 60, hidden: true, alignment: 'right' },
  rrpIndexLocalLead: {
    label: 'LL RRP Index',
    size: 80,
    alignment: 'right',
  },
  lpIndexLocalLead: {
    label: 'LL LP Index',
    size: 80,
    alignment: 'right',
  },
  currency: {
    label: 'LP Currency',
    size: 80,
    hidden: true,
    alignment: 'right',
  },
  priceStatus: {
    label: 'Price Validity',
    size: 85,
    columnType: !isReadOnly && isApproveMode ? 'editable' : undefined,
    alignment: 'right',
  },
  hasFutureLpChange: {
    label: 'Future LP Δ',
    size: 70,
    alignment: 'right',
  },
  listPriceFmcFactor: {
    label: 'LP/FMC Factor',
    size: 85,
    alignment: 'right',
  },
  hasPriceChange: {
    label: 'Price Δ',
    size: 60,
    alignment: 'right',
  },
  globalLeadRrp: {
    label: 'GL RRP',
    size: 70,
    hidden: true,
    alignment: 'right',
  },
  globalLeadCurrency: {
    label: 'GL RRP Currency',
    size: 100,
    hidden: true,
    alignment: 'right',
  },
  rrpIndexGlobalLead: {
    label: 'GL RRP Index',
    size: 80,
    hidden: true,
    alignment: 'right',
  },
  lpIndexGlobalLead: {
    label: 'GL LP Index',
    size: 80,
    hidden: true,
    alignment: 'right',
  },
  fmcLocal: { label: 'FMC', size: 60, alignment: 'right' },
  forecastedDemand: { label: 'SI Pcs', size: 60, alignment: 'right' },
});

export const columnGroups: ColumnGroup<TableColumn>[] = [
  {
    id: 'price-list',
    header: 'Price List',
    columnCategory: 'priceList',
    columnKeys: ['priceUnitId', 'priceUnitName'],
  },
  {
    id: 'product-data-pinned',
    header: 'Product Data',
    columnCategory: 'productData',
    columnKeys: ['productId', 'productName'],
  },
  {
    id: 'product-data',
    header: 'Product Data',
    columnCategory: 'productData',
    columnKeys: [
      'themeName',
      'productAudience',
      'launchStatus',
      'license',
      'productGroup',
      'subMarketGroupName',
      'groupName',
      'productCategory',
      'salesChannel',
      'productClassLvl1',
      'productClassLvl2',
      'developmentOwnershipName',
      'productCompositionName',
      'superSegmentName',
      'themeCategoryName',
      'isRareItem',
      'boxSizeName',
      'businessToBusinessLaunchDate',
      'directToConsumerLifelong',
      'sizeDimension',
      'pieceCount',
      'smartPlayName',
      'playTypeName',
      'topThemeName',
      'localLaunchDate',
      'localExitDate',
      'primaryKey',
    ],
  },
  {
    id: 'local-lead',
    header: 'Local Lead',
    columnCategory: 'localLead',
    columnKeys: [
      'localLeadRrp',
      'localLeadCurrency',
      'localLeadLp',
      'localLeadCurrencyRate',
    ],
  },
  {
    id: 'current-year',
    header: 'Local',
    columnCategory: 'currentYear',
    columnKeys: [
      'rrp',
      'rrpCurrency',
      'rrpIndexLocalLead',
      'rrpIndexGlobalLead',
      'globalLeadRrp',
      'globalLeadCurrency',
      'vat',
      'rm',
      'lp',
      'lpIndexLocalLead',
      'currency',
      'priceStatus',
      'hasFutureLpChange',
      'lpIndexGlobalLead',
      'listPriceFmcFactor',
      'hasPriceChange',
    ],
  },
  {
    id: 'other',
    header: 'Other',
    columnCategory: 'other',
    columnKeys: ['forecastedDemand', 'fmcLocal'],
  },
];
